// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect, Fragment } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import PropTypes, { func, shape } from 'prop-types';
import { get } from 'lodash';
import { push } from 'connected-react-router';

import {
  Footer, TabPanel, TabsContainer,
} from '../../../components';
import { getTabIndex, hasOrgPublisherRole, hasPublisherRole, hasCRUDPermissions } from '../../../utils';
import { getI18n, getI18nFormattedMessage } from '../../../utils/intl';
import {
  getConfig,
  getUserDetails,
} from '../../../reducers/portalConfig';
import {
  getIsSaveApiSuccess,
} from '../../../reducers/api';
import {
  DetailsHeader,
  StatusIndicator,
  AlertDialog,
  RadioGroupSection,
} from '../../../components';
import DetailsContainer from '../../details';
import Overview from './Overview';
import Applications from './Applications';
import Deployments from './Deployments';
import ApiLocation from './ApiLocation';
import Products from './Products';
import Spec from './Spec';
import OrganizationAccess from './OrganizationAccess';
import Actions from './Actions';
import { withStyles } from "@material-ui/core";
import styles from "../Edit/styles";
import {
  fetchApi,
  fetchAPITags,
  fetchAssets,
  fetchApiPermitted,
  deleteApi,
  updateApiDetails,
} from "../../../actions/api";
import {
  getApiCanEditPermitted,
  getApiCanDeletePermitted,
  getApiDetails,
  getAPITags,
  getAssets,
  getDeleteApiStatus,
  getErrors,
} from "../../../reducers/api";

import {
  API_PUBLISH_STATE_TYPES,
  API_PUBLISH_STATE_NEW,
  API_PUBLISH_STATE_INCOMPLETE,
} from '../Edit/labels';
import { ALERT_SUCCESS, PERMISSION_PRODUCTS_API } from '../../../constants';
import Documentation from "../../../pageComponents/Documentations";

const getTabItems = (
  { apiServiceType, publishedByPortal }, userContext, canEdit,
) => {
  const hasRead = hasCRUDPermissions(userContext, ['READ'], PERMISSION_PRODUCTS_API);
  return [
    {
      id: 'overview',
      tabId: 'api-details-overview-tab',
      label: getI18nFormattedMessage('label.overview'),
      visible: true,
    },
    {
      id: 'organizations',
      tabId: 'api-details-org-access-tab',
      label: getI18nFormattedMessage('label.organizations'),
      visible: hasOrgPublisherRole(userContext) || hasPublisherRole(userContext),
    },{
      id: 'applications',
      tabId: 'api-details-applications-tab',
      label: getI18nFormattedMessage('label.applications'),
      visible: true,
    },
    {
      id: 'products',
      tabId: 'api-details-products-tab',
      label: getI18nFormattedMessage('label.products'),
      visible: hasRead,
    },
    {
      id: 'deployments',
      tabId: 'api-details-deployments-tab',
      label: getI18nFormattedMessage('label.deployments'),
      visible: hasOrgPublisherRole(userContext) || hasPublisherRole(userContext),
    },
    {
      id: 'api-locations',
      tabId: 'api-details-api-location-tab',
      label: getI18nFormattedMessage('label.api.location.tab'),
      visible: publishedByPortal && canEdit &&
        (hasOrgPublisherRole(userContext) || hasPublisherRole(userContext)),
    },
    {
      id: 'spec',
      tabId: 'api-details-spec-tab',
      label: getI18nFormattedMessage('label.spec'),
      visible: apiServiceType === 'REST',
    },
    {
      id: 'documentation',
      tabId: 'api-details-documentation-tab',
      label: getI18nFormattedMessage('label.documentation'),
      visible: true,
    },
  ];
}

const getTabs = (tabItems) => tabItems.filter(({ visible }) => visible);

export const ApiDetails = (props) => {
  const {
    userContext,
    canEdit,
    canDelete,
    apiDetails,
    assets,
    apiTags,
    history,
    errors,
    isApiSaveSuccess,
  } = props;

  const intl = getI18n(useIntl());
  const { name, portalStatus, version, accessStatus, modifyTs } = apiDetails;

  const apiUuid = get(props, 'match.params.apiUuid');
  const tabItems = getTabItems(apiDetails, userContext, canEdit);
  const tabs = getTabs(tabItems);
  const defaultIndex = getTabIndex(tabs, props.match);
  const [tabIndex, setTabIndex] = useState(defaultIndex);
  const [currentTab, setCurrentTab] = useState(tabs[defaultIndex].tabId);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [showChangeStatus, setShowChangeStatus] = useState(false);
  const [portalState, setPortalState] = useState('');

  const fetchApiData = async () => {
    await props.fetchApi(apiUuid);
    await props.fetchApiPermitted(apiUuid);
    await props.fetchAPITags(apiUuid);
    await props.fetchAssets(apiUuid);
  };

  useEffect(() => {
    if (apiUuid) {
      fetchApiData();
    }
  }, [apiUuid]);

  useEffect(() => {
    if (isApiSaveSuccess) {
      notifyMessages(ALERT_SUCCESS, getI18nFormattedMessage('label.api.status.success'));
      fetchApiData();
      setTimeout(() => {
        notifyMessages('', '')
      }, 2500);
    }
  }, [isApiSaveSuccess]);

  useEffect(() => {
    if(apiDetails) {
      const defaultIndex = getTabIndex(tabs, props.match);
      setTabIndex(defaultIndex);
      setCurrentTab(tabs[defaultIndex].tabId);
      setPortalState(apiDetails.portalStatus);
    }
  }, [props.match, apiDetails]);

  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  const handleTabChange = (e, newValue) => {
    setTabIndex(newValue);
    setCurrentTab(tabs[newValue].tabId);
      history.push(`/publish/apis/details/${apiUuid}/${tabs[newValue].id}`)
  };

  const onChangeStatus = () => {
    setShowChangeStatus(true);
  };

  const onChangeSubmit = async () => {
    setShowChangeStatus(false);
    const data = apiDetails;
    data.portalStatus = portalState;
    await props.updateApiDetails(apiUuid, data);
  };

  const getErrors = () => {
    // Excluding error which is handled by Product tab.
    const ERR_API_IN_USE = "error.product.api.in.use.by.application"
    return errors.filter(err => err.key !== ERR_API_IN_USE);
  }

  const StatusChange = () => {
    return(
      <RadioGroupSection
        selected={portalState}
        onChange={(e) => setPortalState(e.target.value)}
        data={API_PUBLISH_STATE_TYPES}
        id='api-publish-state'
        isDisable={apiDetails.portalStatus !== API_PUBLISH_STATE_NEW}
        disableOptions={apiDetails.possibleStatuses}
        defaultSelect={apiDetails.portalStatus}
      />
    );
  }

  const Status = () =>
    <StatusIndicator
      status={portalStatus}
      version={version}
      access={accessStatus}
      modifyTs={modifyTs}
      changeStatus={canEdit}
      isChangeButtonDisable={get(apiDetails, 'portalStatus') === API_PUBLISH_STATE_INCOMPLETE
        || get(apiDetails, 'portalStatus') === API_PUBLISH_STATE_NEW}
      onChangeClick={onChangeStatus}
      changeButtonLabel={intl.getI18nMessage('label.change')}
    />;

  return (
    <>
      {showChangeStatus &&
        <AlertDialog
          isOpen={showChangeStatus}
          title={intl.getI18nMessage('label.change.publish.title')}
          description={''}
          onClose={()=> setShowChangeStatus(false)}
          onSubmit={onChangeSubmit}
          onCancel={()=> setShowChangeStatus(false)}
          dialogId={'api-details-change-status'}
          submitText={intl.getI18nMessage('label.save.button')}
          cancelText={intl.getI18nMessage('label.cancel.button')}
          component={<StatusChange />}
        />
      }
      <DetailsContainer
        detailsPageId="api-details-page"
        isLoading={false}
        notificationId="api-details-notifications"
        notificationStatus={notificationStatus}
        setNotificationStatus={setNotificationMessage}
        notificationMessage={notificationMessage}
        setNotificationMessage={setNotificationMessage}
        errors={getErrors()}
        header={
          <DetailsHeader
            headerId="api-details-header-container"
            title={intl.getI18nMessage('label.overview')}
            name={name}
            statusComponent={<Status />}
          />
        }
        actions={
          <Actions
            {...props}
            apiUuid={apiUuid}
            setNotificationMessage={setNotificationMessage}
            setNotificationStatus={setNotificationMessage}
            errors={errors}
          />
        }
        tabs={
          <TabsContainer
            id={'api-details-tab-container'}
            tabValue={tabIndex}
            orientation="horizontal"
            tabItems={tabs}
            handleTabChange={handleTabChange}
          />
        }
        page={
          <Fragment>
            <TabPanel
              visible={currentTab === 'api-details-overview-tab'}
              id="api-overview-tab-panel"
            >
              <Overview
                {...props}
                isCurrentTab={currentTab === 'api-details-overview-tab'}
                userContext={userContext}
                apiUuid={apiUuid}
                canEdit={canEdit}
                apiDetails={apiDetails}
                assets={assets}
                apiTags={apiTags}
                notifyMessages={notifyMessages}
              />
            </TabPanel>
            <TabPanel
              visible={currentTab === 'api-details-applications-tab'}
              id="api-applications-tab-panel"
            >
              <Applications
                {...props}
                isCurrentTab={currentTab === 'api-details-applications-tab'}
                userContext={userContext}
                apiUuid={apiUuid}
                apiDetails={apiDetails}
                notifyMessages={notifyMessages}
              />
            </TabPanel>
            <TabPanel
              visible={currentTab === 'api-details-deployments-tab'}
              id="api-deployments-tab-panel"
            >
              <Deployments
                {...props}
                tabValue={currentTab}
                apiUuid={apiUuid}
                userContext={userContext}
                canEdit={canEdit}
                apiDetails={apiDetails}
                notificationMessage={notificationMessage}
                setNotificationMessage={setNotificationMessage}
                notificationStatus={notificationStatus}
                setNotificationStatus={setNotificationStatus}
              />
            </TabPanel>
            <TabPanel
              visible={currentTab === 'api-details-api-location-tab'}
              id="api-api-location-tab-panel"
            >
              <ApiLocation
                {...props}
                apiUuid={apiUuid}
                apiDetails={apiDetails}
                userContext={userContext}
                notificationMessage={notificationMessage}
                setNotificationMessage={setNotificationMessage}
                notificationStatus={notificationStatus}
                setNotificationStatus={setNotificationStatus}
              />
            </TabPanel>
            <TabPanel
              visible={currentTab === 'api-details-products-tab'}
              id="api-products-tab-panel"
            >
              <Products
                {...props}
                tabValue={currentTab}
                apiUuid={apiUuid}
                userContext={userContext}
                canEdit={canEdit}
                apiDetails={apiDetails}
                notifyMessages={notifyMessages}
              />
            </TabPanel>
            <TabPanel
              visible={currentTab === 'api-details-org-access-tab'}
              id="api-org-access-tab-panel"
            >
              <OrganizationAccess
                {...props}
                tabValue={currentTab}
                apiUuid={apiUuid}
                canEdit={canEdit}
              />
            </TabPanel>
            <TabPanel
              visible={currentTab === 'api-details-spec-tab'}
              id="api-spec-tab-panel"
            >
              <Spec
                {...props}
                apiUuid={apiUuid}
              />
            </TabPanel>
            <TabPanel
              visible={currentTab === 'api-details-documentation-tab'}
              id="api-documentation-tab-panel"
            >
              <Documentation
                {...props}
                entityUuid={apiUuid}
                entityType="api"
                idPrefix="api-details"
                tabValue={currentTab}
                userCanEdit={canEdit}
                userCanDelete={canDelete}
              />
            </TabPanel>
          </Fragment>
        }
        footer={
          <Footer />
        }
      />
    </>
  );
};

const mapStateToProps = state => ({
  config: getConfig(state),
  userContext: getUserDetails(state),
  apiDetails: getApiDetails(state),
  canEdit: getApiCanEditPermitted(state),
  canDelete: getApiCanDeletePermitted(state),
  assets: getAssets(state),
  apiTags: getAPITags(state),
  deleteApiStatus: getDeleteApiStatus(state),
  errors: getErrors(state),
  isApiSaveSuccess: getIsSaveApiSuccess(state),
});

const mapDispatchToProps = {
  push,
  fetchApi,
  fetchAPITags,
  fetchAssets,
  fetchApiPermitted,
  deleteApi,
  updateApiDetails,
};

ApiDetails.propTypes = {
  config: shape({}),
  userContext: shape({}),
  history: shape({}),
  match: shape({}),
  push: func,
  assets: PropTypes.arrayOf(PropTypes.object),
  apiTags: PropTypes.arrayOf(PropTypes.object),
  fetchApi: PropTypes.func,
  fetchAPITags: PropTypes.func,
  fetchAssets: PropTypes.func,
  fetchApiPermitted: PropTypes.func,
  deleteApi: PropTypes.func,
  apiDetails: PropTypes.object,
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  deleteApiStatus: PropTypes.string,
  errors: PropTypes.array,
  updateApiDetails: PropTypes.func,
  isApiSaveSuccess: PropTypes.bool,
};

ApiDetails.displayName = 'ApiDetails';

export default (
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(ApiDetails);
